// Table Pagination Component
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { 
  ChevronLeft, 
  ChevronRight, 
  ChevronsLeft, 
  ChevronsRight,
  MoreHorizontal 
} from "lucide-react";
import { cn } from "@/lib/utils";

export default function TablePagination({ pagination, currentPage, onPageChange }) {
  const [jumpToPage, setJumpToPage] = useState("");

  if (!pagination || pagination.pages <= 1) return null;

  const { total, pages, per_page, has_next, has_prev } = pagination;

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisible = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(pages, startPage + maxVisible - 1);
    
    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    // Always show first page
    if (startPage > 1) {
      pageNumbers.push(1);
      if (startPage > 2) {
        pageNumbers.push('ellipsis-start');
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    // Always show last page
    if (endPage < pages) {
      if (endPage < pages - 1) {
        pageNumbers.push('ellipsis-end');
      }
      pageNumbers.push(pages);
    }

    return pageNumbers;
  };

  const handleJumpToPage = (e) => {
    e.preventDefault();
    const page = parseInt(jumpToPage);
    if (page >= 1 && page <= pages) {
      onPageChange(page);
      setJumpToPage("");
    }
  };

  const startResult = Math.min(total, per_page * (currentPage - 1) + 1);
  const endResult = Math.min(total, per_page * currentPage);

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
      {/* Results Info */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span>Showing</span>
        <Badge variant="secondary" className="font-mono">
          {startResult}-{endResult}
        </Badge>
        <span>of</span>
        <Badge variant="secondary" className="font-mono">
          {total}
        </Badge>
        <span>results</span>
      </div>

      {/* Pagination Controls */}
      <div className="flex flex-wrap items-center gap-2">
        {/* First Page Button */}
        <Button
          variant="outline"
          size="icon"
          onClick={() => onPageChange(1)}
          disabled={!has_prev}
          className="h-9 w-9 hidden sm:flex"
          title="First page"
        >
          <ChevronsLeft className="h-4 w-4" />
        </Button>

        {/* Previous Button */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={!has_prev}
          className="h-9"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          <span className="hidden sm:inline">Previous</span>
          <span className="sm:hidden">Prev</span>
        </Button>

        {/* Page Numbers */}
        <div className="flex items-center gap-1">
          {getPageNumbers().map((pageNum, index) => {
            if (pageNum === 'ellipsis-start' || pageNum === 'ellipsis-end') {
              return (
                <Button
                  key={`ellipsis-${index}`}
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9"
                  disabled
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              );
            }

            return (
              <Button
                key={pageNum}
                variant={pageNum === currentPage ? "default" : "outline"}
                size="sm"
                onClick={() => onPageChange(pageNum)}
                className={cn(
                  "h-9 w-9 font-medium transition-all",
                  pageNum === currentPage && "shadow-md"
                )}
              >
                {pageNum}
              </Button>
            );
          })}
        </div>

        {/* Next Button */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={!has_next}
          className="h-9"
        >
          <span className="hidden sm:inline">Next</span>
          <span className="sm:hidden">Next</span>
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>

        {/* Last Page Button */}
        <Button
          variant="outline"
          size="icon"
          onClick={() => onPageChange(pages)}
          disabled={!has_next}
          className="h-9 w-9 hidden sm:flex"
          title="Last page"
        >
          <ChevronsRight className="h-4 w-4" />
        </Button>

        {/* Jump to Page */}
        {pages > 10 && (
          <form onSubmit={handleJumpToPage} className="hidden lg:flex items-center gap-2 ml-2">
            <span className="text-sm text-muted-foreground">Go to:</span>
            <Input
              type="number"
              min="1"
              max={pages}
              value={jumpToPage}
              onChange={(e) => setJumpToPage(e.target.value)}
              className="h-9 w-16 text-center"
              placeholder={currentPage.toString()}
            />
            <Button 
              type="submit" 
              variant="secondary" 
              size="sm"
              className="h-9"
            >
              Go
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
